// client/src/pages/pmPortal/OwnerBudgetPage.js
// Budget against actual, by month. Twelve figures per account rather than an annual
// number divided by twelve, because the tax bill and the boiler service do not fall
// evenly and a flat twelfth hides variance exactly where an owner needs to see it.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Table, Loading, Notice, Select, money }
    from '../../components/secure/pm/PMKit';

const thisYear = new Date().getFullYear();
const YEARS = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const OwnerBudgetPage = () => {
    const api = usePmPortalApiClient();
    const [year, setYear] = useState(thisYear);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setData(null);
        setError('');
        api.get(`/api/pm-portal/owner/budget/${year}`)
            .then(setData)
            .catch(e => setError(e.message));
    }, [api, year]);

    // One row per account, twelve budget cells and twelve actual cells folded together.
    const rows = React.useMemo(() => {
        if (!data) return [];
        const byAccount = new Map();
        for (const b of data.budget) {
            if (!byAccount.has(b.account_id)) {
                byAccount.set(b.account_id, {
                    account_id: b.account_id, code: b.code, name: b.name,
                    budget: Array(12).fill(0), actual: Array(12).fill(0),
                });
            }
            byAccount.get(b.account_id).budget[b.period_month - 1] += Number(b.amount || 0);
        }
        for (const a of data.actuals) {
            if (!byAccount.has(a.account_id)) continue;
            const row = byAccount.get(a.account_id);
            // Expenses land as debits, income as credits; the net is what the owner spent
            // or earned on that account in the month either way.
            row.actual[a.period_month - 1] += Math.abs(Number(a.debits || 0) - Number(a.credits || 0));
        }
        return [...byAccount.values()].map(r => ({
            ...r,
            budgetTotal: r.budget.reduce((s, n) => s + n, 0),
            actualTotal: r.actual.reduce((s, n) => s + n, 0),
        }));
    }, [data]);

    return (
        <>
            <PageHeader title="Budget vs actual" subtitle="What was planned for the year, and what has happened so far." />

            <Panel title="Year">
                <Select
                    label="Fiscal year"
                    value={year}
                    onChange={e => setYear(Number(e.target.value))}
                    options={YEARS.map(y => ({ value: y, label: String(y) }))}
                    className="max-w-xs"
                />
            </Panel>

            {error && <Notice tone="bad" title="Could not load the budget" message={error} />}
            {!data && !error && <Loading />}

            {data && (
                <>
                    <Panel title={`Annual totals — ${year}`}>
                        <Table
                            columns={[
                                { key: 'code', label: 'Account' },
                                { key: 'name', label: 'Description' },
                                { key: 'budgetTotal', label: 'Budget', align: 'right', render: r => money(r.budgetTotal) },
                                { key: 'actualTotal', label: 'Actual', align: 'right', render: r => money(r.actualTotal) },
                                {
                                    key: 'variance', label: 'Variance', align: 'right',
                                    render: r => {
                                        const v = r.budgetTotal - r.actualTotal;
                                        return <span className={v < 0 ? 'text-rose-700' : 'text-slate-700'}>{money(v)}</span>;
                                    },
                                },
                            ]}
                            rows={rows}
                            empty={`No budget has been set for ${year}.`}
                        />
                    </Panel>

                    {rows.length > 0 && (
                        <Panel title="By month" subtitle="Budget above, actual below, for each account.">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs min-w-max">
                                    <thead>
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left py-2 pr-4 text-[10px] uppercase tracking-wider font-bold text-slate-400">Account</th>
                                            {MONTHS.map(m => (
                                                <th key={m} className="text-right py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">{m}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(r => (
                                            <React.Fragment key={r.account_id}>
                                                <tr className="border-b border-slate-50">
                                                    <td className="py-1.5 pr-4 align-top" rowSpan={2}>
                                                        <div className="font-medium text-slate-800">{r.name}</div>
                                                        <div className="text-slate-400">{r.code}</div>
                                                    </td>
                                                    {r.budget.map((v, i) => (
                                                        <td key={i} className="py-1.5 px-2 text-right tabular-nums text-slate-500">{v ? money(v) : '—'}</td>
                                                    ))}
                                                </tr>
                                                <tr className="border-b border-slate-100">
                                                    {r.actual.map((v, i) => (
                                                        <td key={i} className="pb-2 px-2 text-right tabular-nums font-medium text-slate-800">{v ? money(v) : '—'}</td>
                                                    ))}
                                                </tr>
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    )}
                </>
            )}
        </>
    );
};

export default OwnerBudgetPage;
